import React from "react";

import { ReactFlowProvider } from "@xyflow/react";
import { observer } from "mobx-react-lite";

import { FlowVersion } from "src/api/flowTypes";
import { DocumentTitle } from "src/base-components/DocumentTitle";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { useSyncResponseValidationSchemas } from "src/customConnectionNode/useSyncResponseValidationSchemas";
import { AuthoringUIProvider } from "src/flowContainer/AuthoringUIContext";
import { RightPane } from "src/flowContainer/RightPane";
import { useFlowVersionCopilotContext } from "src/flowContainer/useFlowVersionCopilotContext";
import { VersionAuthoringHeader } from "src/flowContainer/VersionAuthoringHeader";
import { CanvasSearchHighlightProvider } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { DecisionGraph } from "src/flowGraph/DecisionGraph";
import { AuthoringLayout } from "src/layout/AuthoringLayout";
import { OmniboxAuthoring } from "src/omnibox/OmniboxAuthoring";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useSyncInputSchemaDrift } from "src/schema/syncInputSchemaDrift/useSyncInputSchemaDrift";

const GraphAndRightSidePane: React.FC<{
  version: FlowVersion;
}> = ({ version }) => {
  if (!(version.graph_id && version.graph)) {
    return <></>;
  }
  return (
    <>
      <DecisionGraph />
      <RightPane />
    </>
  );
};

export const VersionAuthoringWrapper: React.FC = observer(() => {
  const { flow, version } = useAuthoringContext();

  useSyncInputSchemaDrift(version);
  useSyncResponseValidationSchemas(version);
  useFlowVersionCopilotContext();

  return (
    <ReactFlowProvider>
      <DocumentTitle segments={[version.name, flow.name, "Decision Flows"]} />
      <AuthoringUIProvider>
        <CanvasSearchHighlightProvider>
          <FloatingWindowsProvider>
            <OmniboxAuthoring />
            <AuthoringLayout Header={<VersionAuthoringHeader />}>
              <div
                className="flex h-full w-full flex-row"
                data-loc="graph-and-right-side-pane"
              >
                <GraphAndRightSidePane version={version} />
              </div>
            </AuthoringLayout>
          </FloatingWindowsProvider>
        </CanvasSearchHighlightProvider>
      </AuthoringUIProvider>
    </ReactFlowProvider>
  );
});
